import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { useTranslation } from '../context/LanguageContext';

const { FiMessageSquare, FiSend, FiCopy, FiCheck, FiShare2, FiShield, FiInfo, FiPhone } = FiIcons;
const { FaWhatsapp, FaViber, FaTelegram, FaFacebookMessenger } = FaIcons;

const SMSComposer = ({ location, contacts }) => {
  const [customMessage, setCustomMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation();

  const generateMessage = () => {
    const message = customMessage.trim() || "🚨 EMERGENCY: I need help! My current location:";
    return `${message}\n\n📍 GPS: ${location.latitude.toFixed(6)}, ${location.longitude.toFixed(6)}\n⏰ Time: ${location.timestamp}\n\n🗺️ Google Maps Link:\n${location.googleMapsLink}`;
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(generateMessage());
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDetailedSend = (type, contact) => {
    const message = generateMessage();
    const encodedMessage = encodeURIComponent(message);
    const purePhone = contact.phone.replace(/\D/g, '');
    let url = '';

    if (type === 'sms') url = `sms:${purePhone}?body=${encodedMessage}`;
    else if (type === 'whatsapp') url = `https://wa.me/${purePhone}?text=${encodedMessage}`;

    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleGeneralShare = (type) => {
    const message = generateMessage();
    const encodedMessage = encodeURIComponent(message);
    let url = '';

    if (type === 'whatsapp') url = `https://wa.me/?text=${encodedMessage}`;
    else if (type === 'telegram') url = `https://t.me/share/url?text=${encodedMessage}`;
    else if (type === 'viber') url = `viber://forward?text=${encodedMessage}`;
    else if (type === 'messenger') url = `https://m.me/drrmoiligancity/`;

    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-8">
      <div className="space-y-6">
        <div>
          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-3">
            {t('additional_details')}
          </label>
          <div className="mb-4 p-3 bg-blue-50/50 rounded-xl border border-blue-100 flex items-start space-x-3">
            <SafeIcon icon={FiInfo} className="text-blue-500 mt-0.5" />
            <p className="text-[11px] text-blue-700 leading-relaxed font-medium">{t('emergency_note_desc')}</p>
          </div>
          <textarea 
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
            placeholder={t('emergency_note_placeholder')}
            rows={3}
            className="w-full px-5 py-4 bg-slate-50 border border-slate-100 rounded-[1.5rem] focus:ring-2 focus:ring-indigo-500 focus:bg-white outline-none transition-all resize-none font-medium text-slate-700"
          />
        </div>

        <div className="bg-indigo-50/40 rounded-[2.5rem] p-6 border border-indigo-100/50">
          <h3 className="text-[10px] font-black text-indigo-900 mb-5 flex items-center space-x-2 uppercase tracking-[0.2em]">
            <SafeIcon icon={FiShare2} /> <span>{t('broadcast_title')}</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button onClick={() => handleGeneralShare('messenger')} className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-[#006AFF] text-white rounded-2xl hover:bg-[#0052cc] transition-all shadow-lg font-bold">
              <SafeIcon icon={FaFacebookMessenger} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">CDRRMO</span>
            </button>
            <button onClick={() => handleGeneralShare('whatsapp')} className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-[#25D366] text-white rounded-2xl hover:bg-[#128C7E] transition-all shadow-lg font-bold">
              <SafeIcon icon={FaWhatsapp} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">WhatsApp</span>
            </button>
            <button onClick={() => handleGeneralShare('telegram')} className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-[#0088cc] text-white rounded-2xl hover:bg-[#0077b5] transition-all shadow-lg font-bold">
              <SafeIcon icon={FaTelegram} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">Telegram</span>
            </button>
            <button onClick={() => handleGeneralShare('viber')} className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-[#7360f2] text-white rounded-2xl hover:bg-[#5d4dbd] transition-all shadow-lg font-bold">
              <SafeIcon icon={FaViber} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">Viber</span>
            </button>
            <a href="tel:09177262692" className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-orange-600 text-white rounded-2xl hover:bg-orange-700 transition-all shadow-lg font-bold">
              <SafeIcon icon={FiPhone} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">Globe</span>
            </a>
            <a href="tel:09692337878" className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-orange-500 text-white rounded-2xl hover:bg-orange-600 transition-all shadow-lg font-bold">
              <SafeIcon icon={FiPhone} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">Smart</span>
            </a>
            <a href="tel:09177127411" className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-blue-700 text-white rounded-2xl hover:bg-blue-800 transition-all shadow-lg font-bold">
              <SafeIcon icon={FiShield} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">PNP Globe</span>
            </a>
            <a href="tel:09985987004" className="flex flex-col items-center justify-center space-y-2 px-4 py-4 bg-blue-600 text-white rounded-2xl hover:bg-blue-700 transition-all shadow-lg font-bold">
              <SafeIcon icon={FiShield} className="text-xl" />
              <span className="text-[9px] uppercase tracking-wider">PNP Smart</span>
            </a>
          </div>
        </div>

        <div>
          <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-4">
            {t('direct_messaging')}
          </label>
          <div className="space-y-3">
            {contacts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-slate-100 rounded-[2rem] text-slate-400 font-medium text-sm">
                {t('no_contacts')}
              </div>
            ) : (
              contacts.map((contact) => (
                <div key={contact.id} className="p-5 border border-slate-100 bg-white rounded-[1.5rem] hover:shadow-xl transition-all group flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white font-black text-xl shadow-lg">
                      {contact.name.charAt(0)}
                    </div>
                    <div>
                      <p className="font-black text-slate-900 leading-none mb-1.5">{contact.name}</p>
                      <p className="text-[10px] text-slate-400 font-mono tracking-wider">{contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => handleDetailedSend('sms', contact)} className="p-3.5 bg-slate-50 hover:bg-slate-950 hover:text-white text-slate-600 rounded-xl transition-all border border-slate-200">
                      <SafeIcon icon={FiSend} />
                    </button>
                    <button onClick={() => handleDetailedSend('whatsapp', contact)} className="p-3.5 bg-green-50 hover:bg-[#25D366] hover:text-white text-green-600 rounded-xl transition-all border border-green-100">
                      <SafeIcon icon={FaWhatsapp} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="pt-8 border-t border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">{t('data_payload_preview')}</label>
            <button onClick={copyMessage} className="flex items-center space-x-2 text-[10px] font-black text-indigo-600 hover:text-indigo-700 uppercase tracking-widest transition-colors">
              <SafeIcon icon={copied ? FiCheck : FiCopy} />
              <span>{copied ? t('copied') : t('copy_payload')}</span>
            </button>
          </div>
          <div className="bg-slate-950 rounded-[1.5rem] p-6 shadow-2xl overflow-hidden relative">
            <div className="absolute top-0 right-0 p-3 opacity-10">
              <SafeIcon icon={FiShield} className="text-white text-4xl" />
            </div>
            <code className="block whitespace-pre-wrap text-[11px] leading-relaxed text-indigo-300 font-mono">{generateMessage()}</code>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSComposer;